import { useLanguageStore } from '../stores/useLanguageStore';
import englishMessagesDefault from '../i18n/en';
import vietnameseMessagesDefault from '../i18n/vi';
import { useMemo } from 'react';

// Handle both default export and named export
const getMessages = (messages: any) => {
  if (!messages) return {};
  if (typeof messages === 'object' && 'feedback' in messages) {
    return messages;
  }
  if (messages.default && typeof messages.default === 'object') {
    return messages.default;
  }
  return messages;
};

const englishMessages = getMessages(englishMessagesDefault);
const vietnameseMessages = getMessages(vietnameseMessagesDefault);

// Simple polyglot-like implementation
class SimplePolyglot {
  private phrases: any;
  private locale: string;

  constructor(options: { locale: string; phrases: any }) {
    this.locale = options.locale;
    this.phrases = options.phrases;
  }

  t(key: string, options?: any): string {
    const keys = key.split('.');
    let value = this.phrases;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`❌ Translation missing for key: ${key} (failed at: ${k})`);
        }
        return key;
      }
    }

    if (typeof value !== 'string') {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `❌ Translation value is not a string for key: ${key} (type: ${typeof value})`
        );
      }
      return key;
    }

    // Handle interpolation
    if (options) {
      return value.replace(/%\{(\w+)\}/g, (match: string, key: string) => {
        return options[key] !== undefined ? String(options[key]) : match;
      });
    }

    return value;
  }
}

const messages: Record<string, any> = {
  en: englishMessages,
  vi: vietnameseMessages,
};

export const useTranslation = () => {
  const { locale } = useLanguageStore();

  const polyglot = useMemo(() => {
    const selectedMessages =
      messages[locale] || messages['vi'] || vietnameseMessages;

    return new SimplePolyglot({
      locale: locale || 'vi',
      phrases: selectedMessages || {},
    });
  }, [locale]);

  const translate = useMemo(() => {
    return (key: string, fallbackOrOptions?: string | any, options?: any) => {
      let translated: string;
      if (typeof fallbackOrOptions === 'string') {
        translated = polyglot.t(key, options);
        translated = translated === key ? fallbackOrOptions : translated;
      } else {
        translated = polyglot.t(key, fallbackOrOptions);
      }

      // Handle interpolation with {{}} format (for compatibility)
      if (options && typeof translated === 'string') {
        translated = translated.replace(
          /\{\{(\w+)\}\}/g,
          (match: string, key: string) => {
            return options[key] !== undefined ? String(options[key]) : match;
          }
        );
      }

      return translated;
    };
  }, [polyglot]);

  return { translate, locale };
};

